<script lang="ts" setup>
    import { ref,watch,computed } from 'vue';
    import { ValidationState} from "../lib/Types/Types";
    import TextInput from '../components/TextInput.vue';
    import * as FormattingNumber from "../lib/Formatting/Number"
    import * as Validation from "../lib/Validation"
    import { Alignment} from '../Enums/ChoiceEnums';
import { TextInputType } from '../Enums/UiEnums';
 
    const props = withDefaults(defineProps<{
        numberValue?: number,
        maxLength? : number,
        disabled?: boolean,
        required?: boolean,
        LeftAddon?: string,
        RightAddon?: string
        startingRange?: number;
        endingRange?: number,
        positive?: boolean,
        alignment?: Alignment,
        errorDisplay?: boolean,
        cssFramework?: "bootstrap" | "vanilla"
        minPrecision?: number,
        maxPrecision?: number,
        locale?: string
        placeholder?: string
    }>(),
    {
      disabled: false,
      required: false,
      positive: false,
      alignment: Alignment.right
    });

    const internalNumberValue = ref<string>();   
    const startingRange = computed(()=> props.positive ? 0 : props.startingRange);
    const enableLeftAddonFA = computed(()=> props.LeftAddon && props.LeftAddon.indexOf("fa-") !== -1 ? true : false);
    const enableLeftAddon = computed(()=> props.LeftAddon && props.LeftAddon.indexOf("fa-") === -1 ? true : false);
    const enableRightAddonFA = computed(()=> props.RightAddon && props.RightAddon.indexOf("fa-") !== -1 ? true : false);
    const enableRightAddon = computed(()=> props.RightAddon && props.RightAddon.indexOf("fa-") === -1 ? true : false);
    const textInput = ref();
    
    if (props.numberValue) {
      internalNumberValue.value = props.numberValue.toString();
    }
    

    const emit = defineEmits<{
      (e: "update:numberValue", strValue: number|undefined): void, 
      (e: "change:numberValue", strValue: number|undefined): void, 
      (e: "validationState", state: ValidationState, msg?: string): void,
      (e: "focus", event: FocusEvent): void,
      (e: "blur", event: FocusEvent): void
    }>();

    defineExpose({focus});
 
    watch(() => props.numberValue, (_new, _old) => {
      if (_new !== _old){
        if (undefined === _new || null === _new) {
          internalNumberValue.value = undefined;
        }
        else {
          internalNumberValue.value = "" + _new;
        }
      }          
    });
   
    function onUpdate(textValue?: string | number | undefined)  { 
      if (undefined === textValue) {        
        //in some cases, a currency input can legitimately be undefined, so emit that back
        emit("update:numberValue", textValue);
      }

      if (undefined !== textValue && !isNaN(+textValue)) {
        emit("update:numberValue", +textValue);
      }
    }
    function onChange(textValue?: string | number | undefined)  { 
      if (undefined === textValue) {
        //in some cases, a currency input can legitimately be undefined, so emit that back
        emit("change:numberValue", textValue);
      }

      if (undefined !== textValue && !isNaN(+textValue)) {
        emit("change:numberValue", +textValue);
      }
    }
    function validationLimit(char: string, rawValue: string | undefined): boolean
    {
      if (props.positive) {
        return Validation.LimitToFloatPositive(char,rawValue);
      }
      else {
        return Validation.LimitToFloat(char,rawValue);
      }
    }
    function numberFormatter(value: any, hasFocus?: boolean)  : string|undefined {
      if (undefined === value) {
        return value;
      }
      if (!hasFocus) {
        hasFocus = false;
      }
      const hasDecimal = value.indexOf(".") > -1;
      const [integers, decimals = ""] = value.split(".");
      const min_decimal_precision = (props.minPrecision == undefined ? 2 : props.minPrecision);
      const max_decimal_precision = (props.maxPrecision == undefined ? 2 : props.maxPrecision);
      if (hasFocus) {
        const formatting_options = {locale: props.locale};
        return `${FormattingNumber.NumberToLocaleString(+integers,formatting_options)}${hasDecimal ? "." : ""}${decimals?.substring(0,min_decimal_precision) ?? ""}`; 
      }
      else {
        const formatting_options = {locale: props.locale,minimumFractionDigits: min_decimal_precision, maximumFractionDigits: max_decimal_precision};
        return `${FormattingNumber.NumberToLocaleString(+value,formatting_options)}`; 
      }
    }
    function normalizer(rawText: string|undefined) : any {      
      return rawText?.match(/-?\d|\./g)?.join("");
    }

    function focus()
    {
        textInput.value?.focus();
    }

</script>

<template>
    <TextInput 
        ref="textInput"
        v-model:textValue="internalNumberValue"  
        :maxLength="props.maxLength" 
        :disabled="props.disabled"  
        :required="props.required" 
        :alignment="props.alignment"
        :errorDisplay="props.errorDisplay"
        :placeholder="props.placeholder"
        :acceptCharacter="validationLimit"
        :validator="Validation.InRangeFunction(startingRange, props.endingRange)"
        :formatter="numberFormatter"
        :normalizer="normalizer"
        :cssFramework="props.cssFramework"
        @update:textValue="onUpdate"
        @change:textValue="onChange"
        @validationState="(state, msg) => emit('validationState', state, msg)"      
        @blur="evt => emit('blur',evt)"
        @focus="evt => emit('focus',evt)"
    >
      <template v-if="enableLeftAddonFA" v-slot:LeftAddon><em :class=props.LeftAddon></em></template>
      <template v-if="enableRightAddonFA" v-slot:RightAddon><em :class=props.RightAddon></em></template>
      <template v-if="enableLeftAddon" v-slot:LeftAddon>{{props.LeftAddon}}</template>
      <template v-if="enableRightAddon" v-slot:RightAddon>{{props.RightAddon}}</template>
    </TextInput>
</template>